import {getFirestore, collection, doc, getDoc} from 'firebase/firestore';
import {getAuth} from 'firebase/auth';
import type {User as FirebaseUser} from 'firebase/auth';
import {CompanyDoc, CompanyDocRef, UserDoc} from '../types';

export interface IUserService {
  currentFirebaseAuthUser(): Promise<FirebaseUser | null>;
  isAnonymous(): Promise<boolean>;
  currentUser(): Promise<UserDoc | null>;
  currentUserCompany(): Promise<CompanyDocRef | null>;
}

export class User implements IUserService {
  private static _instance: User | null = null;

  private constructor() {
    return;
  }

  public static get instance(): User {
    if (!User._instance) User._instance = new User();
    return User._instance;
  }

  public currentFirebaseAuthUser(): Promise<FirebaseUser | null> {
    return new Promise(resolve => {
      const unsubscribe = getAuth().onAuthStateChanged(user => {
        resolve(user);
        unsubscribe();
      });
    });
  }

  public async isAnonymous(): Promise<boolean> {
    const user = await this.currentFirebaseAuthUser();
    if (user === null) return true;
    return !user.email && !user.phoneNumber;
  }

  public async currentUser(): Promise<UserDoc | null> {
    const [authUser, isAnonymous] = await Promise.all([
      this.currentFirebaseAuthUser(),
      this.isAnonymous(),
    ]);

    if (authUser === null || isAnonymous) {
      // defaults:
      return null;
    } else {
      if (authUser.uid !== '0') {
        const firestore = getFirestore();

        const userSnap = await getDoc(
          doc(collection(firestore, 'users'), authUser.uid),
        );
        if (userSnap.exists()) {
          return userSnap.data() as UserDoc;
        }
      }

      return null;
    }
  }

  public async currentUserCompany(): Promise<CompanyDocRef | null> {
    const user = await this.currentUser();
    if (!user || !user.company) {
      return null;
    }

    const firestore = getFirestore();
    const companySnap = await getDoc(doc(firestore, user.company.path));
    if (companySnap.exists()) {
      return {
        __ref__: companySnap.ref,
        ...(companySnap.data() as CompanyDoc),
      };
    }

    return null;
  }
}
